import Vue from 'vue'
import Meta from 'vue-meta'
import VueBrowserUpdate from 'vue-browserupdate'
import PMDApp from './pmd/PMDApp.vue'
import router from './routes'
import store from './store'
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import 'firebase/compat/firestore'
import 'firebase/compat/performance'
import 'firebase/compat/messaging'
import 'firebase/compat/storage'
import { highchartsTheme } from './util/bootstrap_helper'
import {
  phoneValidator,
  strongPwdValidator,
  uniqueEmailValidator,
  emailOrPhoneValidator,
  emailValidator,
  twilioPhoneValidator,
  twilioPhoneExcludeHostedValidator,
  handlebarsValidator,
  urlValidator,
  newUrlValidator,
  urlToEmptyValidator,
  notEmptyValidator,
  requiredField,
} from './util/validators'
import {
  pythonBackend,
  membershipBackend,
  snapshotBackend,
  snapshotsRevexService,
  dialogflowEngagements,
  paymentService,
  publicApi,
  saasService,
  isvService,
  humanRollOverService,
  interceptorWithBothToken,
  appengineBackend,
  revexBackendService,
  emailBackend
} from './util/axios_interceptors'
import VueConfetti from 'vue-confetti'
import { getRemoteConfig, fetchAndActivate } from 'firebase/remote-config'
import config from './config'
import axios from 'axios'
import VeeValidate, { Validator } from 'vee-validate'
import { User } from './models'
import Vue2Filters from 'vue2-filters'
import VueHighcharts from 'vue-highcharts'
import Highcharts from 'highcharts'
import Maps from 'highcharts/modules/map'
import loadHighchartsMore from 'highcharts/highcharts-more'
import loadSolidGauge from 'highcharts/modules/solid-gauge'
import NoDataModule from 'highcharts/modules/no-data-to-display.js'
import NotificationHelper from './util/notification_helper'
import UxMessagePlugin from './plugins/UxMessagePlugin'
import infiniteScroll from 'vue-infinite-scroll'
import VueCookie from 'vue-cookie'
import LoaderSlot from './pmd/components/LoaderSlot'
import MoonLoader from './pmd/components/MoonLoader'
import MoonLoaderTitle from './pmd/components/MoonLoaderTitle'
import draggable from 'vuedraggable'
import { VueMasonryPlugin } from 'vue-masonry'
import '@/mixins/globalmixin'
import localStorage from 'store'
import VueExpandableImage from 'vue-expandable-image'
import { OauthService } from '@/services'
import VTooltip from 'v-tooltip'
import {
  dispatchCustomEvents,
  refreshFirebaseToken,
  handleExternalRedirect,
  dispatchConversationsV2SyncAll,
  dispatchSyncAlls,
  getHTMLElements,
  setSidebarState,
} from './util/router_helper'
import { translationsDiffChecker } from './localization/utils'
import { fetchCompanyData, resetInactivityTimer, storageEventListener, unhandledRejectionEventListener } from './util/bootstrap_helper'
import { registerNotificationActionHandlers } from './util/notification/NotificationActionService'
import { ensureActiveFirebaseAuthState } from './util/firebase-auth'


// Global vue event bus to transmit/listen event globally
Vue.prototype.$bus = new Vue({
  name: 'global-event-bus'
})
// Using Vtooltip to render raw html content. Not possible with current tootltip lib
Vue.use(VTooltip)
Vue.component('loader-slot', LoaderSlot)
Vue.component('moon-loader', MoonLoader)
Vue.component('moon-loader-title', MoonLoaderTitle)
Vue.component('draggable', draggable)
Vue.use(VueConfetti)
import './plugins/setupEmitter'
// array helper
require('./util/array_helper')

import BootstrapVue from 'bootstrap-vue'

Vue.use(BootstrapVue)
Vue.use(VueExpandableImage)
Vue.use(VueCookie)

Maps(Highcharts)

// tailwindcss

import './assets/pmd/css/tailwind.css'
import { registerComponents } from './pmd/components/UIComponents'
Vue.use(registerComponents)
// Vue composition API for vue 2
import VueCompositionAPI from '@vue/composition-api'
Vue.use(VueCompositionAPI)

import VueFinalModal from 'vue-final-modal'

Vue.use(VueFinalModal())

import Notifications from 'vue-notification'
import velocity from 'velocity-animate'
import './util/async_loading_remoteApps'
import './util/runtime_loading_remoteApps'
Vue.use(Notifications, { velocity })

Highcharts['theme'] = highchartsTheme
// Apply the theme
Highcharts.setOptions(Highcharts['theme'])
loadHighchartsMore(Highcharts)
loadSolidGauge(Highcharts)
NoDataModule(Highcharts)
Vue.use(VueHighcharts, { Highcharts })

Vue.use(infiniteScroll)
Vue.config.devtools = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'staging' || process.env.NODE_ENV === 'interop'

Vue.config.productionTip = process.env.NODE_ENV === 'development'

Vue.config.ignoredElements = ['chat-widget']


Vue.use(Meta)
Vue.use(VueBrowserUpdate, {
  options: {},
})
Vue.use(Vue2Filters)

Vue.use(UxMessagePlugin, {
  rootSelector: '#app',
})

Vue.use(VueMasonryPlugin)

const firebaseApp = firebase.initializeApp(config.firebase)

setupAuth()

if ('serviceWorker' in navigator) {
  let serviceWorkerFilename:string
  if (config.mode === 'dev' || config.mode === 'staging' || config.mode === 'interop') {
    serviceWorkerFilename = '/pmd-fcm-sw-staging.js'
  } else {
    serviceWorkerFilename = '/pmd-fcm-sw.js'
  }
  navigator.serviceWorker
    .register(serviceWorkerFilename)
    .then(registration => {
      NotificationHelper.init(registration)
    })
    .catch(err => {
      // handle catch
    })
}

if (localStorage.get('proxyLoginCount') === undefined) {
  localStorage.set('proxyLoginCount', 0)
}

window.addEventListener('storage', storageEventListener)
window.addEventListener('unhandledrejection', unhandledRejectionEventListener)

const activityEvents = ['mousemove', 'keydown', 'click']
activityEvents.forEach(event =>
  window.addEventListener(event, resetInactivityTimer)
)

firebase.auth().onAuthStateChanged(async user => {
  try {
    // Listener is potentially restoring tokens, skip auth processing for logout route
    const isLogoutRoute = router.currentRoute?.params?.logout === 'true'
    if (isLogoutRoute) {
      return
    }

    // Skip auth processing for support agent login route to prevent API calls with old tokens
    if (router.currentRoute?.name === 'login_support') {
      return
    }

    if (user) {
      const auth = await store.dispatch('auth/get')
      if (auth && auth.userId && !auth.authToken) {
        console.log('JWT not exist')
        const authWithJwt = await OauthService.getCurrentUser()
        if (!authWithJwt?.authToken || !authWithJwt?.refreshToken) {
          throw new Error('Not able to get JWT tokens for the User')
        }
        const authSetData = {
          firebaseToken: authWithJwt.firebaseToken,
          jwt: authWithJwt.jwt,
          refreshJwt: authWithJwt.refreshJwt,
          apiKey: authWithJwt.apiKey,
          userId: authWithJwt.userId,
          companyId: authWithJwt.companyId,
          authToken: authWithJwt.authToken,
          refreshToken: authWithJwt.refreshToken,
        }
        await store.dispatch('auth/set', authSetData)
      } else {
        // if query has app=canny, consider it as a canny app sso login
        const queryParams = new URLSearchParams(window.location.search)
        const cannyCompanyId = queryParams.get('companyID')
        const cannyRedirectUrl = queryParams.get('redirect')
        const isCannyLogin = queryParams.get('app') === 'canny' && cannyCompanyId && cannyRedirectUrl

        if (isCannyLogin && auth.userId) {
          try {
            const response = await OauthService.getCannySSOLoginToken(auth.userId)
            const cannySSOLoginToken = response.cannySSOLoginToken
            // redirect to canny
            const cannySSOUrl = `https://canny.io/api/redirects/sso?ssoToken=${cannySSOLoginToken}&companyID=${cannyCompanyId}&redirect=${cannyRedirectUrl}`
            window.location.assign(cannySSOUrl)
            window.location.href = cannySSOUrl
          } catch (e) {
            console.error('Bootstrap - Error while getting canny sso login token', e)
          }
        }
      }
    }
  } catch (error) {
    console.warn('Error while setting tokens', error)
  }
})

const VueCtkDateTimePicker = () => import('vue-ctk-date-time-picker')
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import { setupAuth } from './util/setup_auth'
import { subAccountRoutes } from '@/routes/constants/agency/subAccounts'

Vue.component('vue-ctk-date-time-picker', VueCtkDateTimePicker)

Vue.prototype.$private = {}

const remoteConfig = getRemoteConfig(firebaseApp)
remoteConfig.settings.minimumFetchIntervalMillis = 3600000
remoteConfig.settings.fetchTimeoutMillis = 60000
fetchAndActivate(remoteConfig)
  .then(() => {
    console.log('app init')
  })
  .catch(err => {
    console.error('remote config failed: ', err)
  })
Vue.prototype.remoteConfig = remoteConfig
Vue.prototype.firebaseApp = firebaseApp
axios.defaults.baseURL = config.baseUrl
axios.interceptors.request.use(interceptorWithBothToken)
Vue.prototype.$http = axios
Vue.prototype.pythonBackend = pythonBackend
Vue.prototype.membershipBackend = membershipBackend
Vue.prototype.$snapshotBackend = snapshotBackend
Vue.prototype.$snapshotsRevexService = snapshotsRevexService
Vue.prototype.dialogflowEngagements = dialogflowEngagements
Vue.prototype.paymentService = paymentService
Vue.prototype.publicApi = publicApi
Vue.prototype.saasService = saasService
Vue.prototype.isvService = isvService
Vue.prototype.revexBackendService = revexBackendService
Vue.prototype.humanRollOverService = humanRollOverService
Vue.prototype.$appengineBackend = appengineBackend
Vue.prototype.emailBackend = emailBackend

Validator.extend('phone', phoneValidator)
Validator.extend('strongPassword', strongPwdValidator)
Validator.extend('uniqueEmail', uniqueEmailValidator)
Validator.extend('emailOrPhone', emailOrPhoneValidator)
Validator.extend('email', emailValidator)
Validator.extend('twilioPhone', twilioPhoneValidator)
Validator.extend('twilioPhoneExcludeHosted', twilioPhoneExcludeHostedValidator)
Validator.extend('required', requiredField)

Vue.use(VeeValidate, {
  classes: true,
  events: 'input|blur',
  fieldsBagName: 'veevalidateFields',
})

Validator.extend('handlebars', handlebarsValidator)

Validator.extend('validUrl', urlValidator)
Validator.extend('newValidUrl', newUrlValidator)

Validator.extend('validUrlToEmpty', urlToEmptyValidator)

Validator.extend('notEmpty', notEmptyValidator)

import { Languages, i18n, updateLocale } from './localization'
import { Modules } from './util/modules'
import { initializeSentry } from './plugins/sentry'
import { initDebugging } from './util/performance-debugger/initDebugger'

initializeSentry(Vue, router, config, true) // remove in prod
store.commit('setAgencyLoaderState', true)

initDebugging(router)

// Register notification action handlers for the application
registerNotificationActionHandlers(store)

router.beforeEach(async (to, from, next) => {
  // Skip all the logic for logout page, it can create race condition with the auth state change
  const isLogout = to.params?.logout === 'true'
  if(isLogout) {
    return next()
  }

  // Skip all API calls for support agent login route to prevent calls with old tokens
  const unauthenticatedPages = [
    'login', 'login_sso', 'login_page', 'login_support'
  ]
  const isUnauthenticatedPage = unauthenticatedPages.includes(to.name || '')
  if(isUnauthenticatedPage) return next()

  // External redirect
  if (to.matched.some(record => record.meta.RedirectExternalUrl)) {
    await handleExternalRedirect(to)
    return
  }
  const fromLocationId = from?.params?.location_id
  let toLocationId = to.params.location_id,
    accountId
  if (to.name === subAccountRoutes.accounts.detail) toLocationId = to.params.account_id
  const user = new User(await store.dispatch('user/get'))

  await setupLocale(to, from)

  if (toLocationId && user.type === 'account' && user.locations[toLocationId] === undefined) {
    console.log(`you are not authorized to access this location: ${toLocationId}`)
    router.push({ name: 'dashboard' })
    return
  }

  await refreshFirebaseToken(toLocationId)

  // Check if firebase auth instance exists and has proper access to the location
  try {
    await ensureActiveFirebaseAuthState(toLocationId)
  } catch (e) {
    console.error('Bootstrap - Error while checking firebase token', e)
  }

  await dispatchConversationsV2SyncAll(to, from)

  if (fromLocationId !== toLocationId && toLocationId) {
    const eventParams = {
      detail: {
        from: fromLocationId,
        to: toLocationId,
      },
    }
    dispatchCustomEvents('locationChangeEvent', eventParams)

    const [element, htmlTag] = getHTMLElements()
    store.commit('setLocationLoaderState', true)
    try {
      await dispatchSyncAlls(toLocationId, user.id)

      if (element) element.className = ''
      if (htmlTag) htmlTag.className = ''
    } catch (error) {
      console.error('Error while syncing all', error)
    } finally {
      store.commit('setLocationLoaderState', false)
    }
  } else if (accountId !== to.params.account_id) {
    accountId = to.params.account_id
    const [element, htmlTag] = getHTMLElements()
    await refreshFirebaseToken(accountId)
    if (element) element.className = ''
    if (htmlTag) htmlTag.className = ''
  }

  setSidebarState(to, from)

  next()
})

var firstLoadCompleted = false

if (config.googleAnalyticId) {
  ga('create', config.googleAnalyticId, 'auto')
  ga('set', 'page', router.currentRoute.name)
  ga('send', 'pageview')
}

// Removing Default App Loader...
router.afterEach(async (to, from) => {
  await setupLocale(to, from)

  if (!firstLoadCompleted) {
    Vue.nextTick(() => {
      // route is now ready.
      const app = document.getElementById('app')
      // const htmlTag = document.getElementsByTagName('html')[0]
      if (app && !['login', 'forgot_password'].includes(to.name || '')) app.className = ''
      // htmlTag.className = ''
      store.commit('setAgencyLoaderState', false)
      firstLoadCompleted = true
      // console.log('nextTick');
    })
    window.dispatchEvent(
      new CustomEvent('routeLoaded', {
        detail: {
          to: {
            fullPath: to.fullPath,
            name: to.name,
            params: to.params,
            path: to.path,
            query: to.query,
          },
          from: {
            fullPath: from?.fullPath,
            name: from?.name,
            params: from?.params,
            path: from?.path,
            query: from?.query,
          },
        },
      })
    )
  }
  if (config.googleAnalyticId) {
    if (to.name !== from.name) {
      ga('set', 'page', to.name)
      ga('send', 'pageview')
    }
  }

  if (CustomEvent !== undefined) {
    window.dispatchEvent(
      new CustomEvent('routeChangeEvent', {
        detail: {
          to: {
            fullPath: to.fullPath,
            name: to.name,
            params: to.params,
            path: to.path,
            query: to.query,
          },
          from: {
            fullPath: from.fullPath,
            name: from.name,
            params: from.params,
            path: from.path,
            query: from.query,
          },
        },
      })
    )
   }
})

store.subscribe(async (mutation,state)=>{
  if (mutation.type === 'company/set') {
    await fetchCompanyData()
  }
})

new Vue({
  router,
  store,
  i18n,
  render: h => h(PMDApp),
}).$mount('#app')

export async function setupLocale(to, from) {
  try {
    const userSpecificLanguage = store.state.user?.user?.platformLanguage
    const locationLanguage = store.state.locations?.currentLocation?.locale_settings?.platform
    const languageChosen = (function () {
      if (userSpecificLanguage) return userSpecificLanguage
      if (locationLanguage) return locationLanguage
      return Languages.English_US
    })()

    // Check the diff
    const translationToLoad = translationsDiffChecker(to, from)

    if (translationToLoad?.length) {
      await updateLocale(languageChosen as Languages, translationToLoad as Modules[])
      return true
    } else if (!firstLoadCompleted) {
      // check for better option
      await updateLocale(languageChosen as Languages, [Modules.Common,Modules.SmartList, Modules.ContactDetail, Modules.Copilot])
    }
  } catch (error) {
    console.error(error)
  }
}
store.$http = axios
